import { useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const originIcon = new L.DivIcon({
  className: "",
  html: `<div style="width:32px;height:32px;background:#FF5200;border:3px solid white;border-radius:50%;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3"/></svg>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

const destIcon = new L.DivIcon({
  className: "",
  html: `<div style="width:32px;height:40px;position:relative;">
    <div style="width:32px;height:32px;background:#09090B;border:3px solid white;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;">
      <svg style="transform:rotate(45deg)" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
    </div>
  </div>`,
  iconSize: [32, 40],
  iconAnchor: [16, 40],
});

function FitBounds({ origin, destination }) {
  const map = useMap();
  const fitted = useRef(false);

  useEffect(() => {
    if (origin && destination && !fitted.current) {
      const bounds = L.latLngBounds([origin, destination]);
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 10 });
      fitted.current = true;
    }
  }, [map, origin, destination]);

  useEffect(() => {
    fitted.current = false;
  }, [origin, destination]);

  return null;
}

function buildCurve(start, end, numPoints = 40) {
  const points = [];
  const midLat = (start[0] + end[0]) / 2;
  const midLng = (start[1] + end[1]) / 2;
  const dx = end[1] - start[1];
  const dy = end[0] - start[0];
  const dist = Math.sqrt(dx * dx + dy * dy);
  const offset = dist * 0.15;
  const ctrlLat = midLat + (dx / dist) * offset;
  const ctrlLng = midLng - (dy / dist) * offset;

  for (let i = 0; i <= numPoints; i++) {
    const t = i / numPoints;
    const lat = (1 - t) * (1 - t) * start[0] + 2 * (1 - t) * t * ctrlLat + t * t * end[0];
    const lng = (1 - t) * (1 - t) * start[1] + 2 * (1 - t) * t * ctrlLng + t * t * end[1];
    points.push([lat, lng]);
  }
  return points;
}

export default function RouteMap({ originCoords, destCoords, originName, destName, distance }) {
  if (!originCoords || !destCoords) return null;

  const curvePoints = buildCurve(originCoords, destCoords);
  const center = [(originCoords[0] + destCoords[0]) / 2, (originCoords[1] + destCoords[1]) / 2];

  return (
    <div className="relative" data-testid="route-map">
      <div className="absolute top-3 left-3 z-[1000] bg-white/95 backdrop-blur-sm border-2 border-zinc-200 px-4 py-2.5 shadow-md">
        <div className="flex items-center gap-3 text-sm font-body">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-[#FF5200] inline-block" />
            <strong>{originName}</strong>
          </span>
          <svg width="20" height="12" viewBox="0 0 20 12" className="text-zinc-400">
            <path d="M0 6h16M12 1l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm bg-[#09090B] inline-block" style={{ borderRadius: "50% 50% 50% 0", transform: "rotate(-45deg)", width: 10, height: 10 }} />
            <strong>{destName}</strong>
          </span>
          <span className="text-zinc-400 ml-1">~{Math.round(distance)} km</span>
        </div>
      </div>
      <MapContainer
        center={center}
        zoom={6}
        scrollWheelZoom={false}
        style={{ height: 340, width: "100%" }}
        className="rounded-sm border-2 border-zinc-200"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
        />
        <Marker position={originCoords} icon={originIcon} />
        <Marker position={destCoords} icon={destIcon} />
        <Polyline
          positions={curvePoints}
          pathOptions={{ color: "#FF5200", weight: 3, opacity: 0.8, dashArray: "8 6" }}
        />
        <FitBounds origin={originCoords} destination={destCoords} />
      </MapContainer>
    </div>
  );
}
